/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Subscription Management, Orders & Invoices Store (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type SubscriptionStatus = 'active' | 'canceled' | 'past_due';
export type BillingInterval = 'monthly' | 'yearly';
export type PaymentGateway = 'stripe' | 'bank_transfer' | 'paypal' | 'crypto';
export type PlanTier = 'free' | 'pro' | 'enterprise';

export interface PlanItem {
  id: string;
  tier: PlanTier;
  name: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  creditsIncluded: number;
  features: string[];
  isFeatured: boolean;
  seats: number;
}

export interface TokenPackItem {
  id: string;
  name: string;
  credits: number;
  price: number;
  badge?: string;
}

export interface Subscription {
  id: string;
  workspaceId: string;
  planId: string;
  tier: PlanTier;
  gatewayCode: PaymentGateway;
  gatewaySubId: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  monthlyCredits: number;
  creditsUsedThisPeriod: number;
  paymentMethodPreview: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
  };
}

export interface Order {
  id: string;
  invoiceNumber: string;
  workspaceId: string;
  planId: string;
  description: string;
  amountPaid: number;
  taxAmount: number;
  currency: 'USD' | 'VND';
  gatewayCode: PaymentGateway;
  gatewayTransactionId: string;
  status: 'paid' | 'pending' | 'refunded' | 'failed';
  proofFileR2?: string;
  createdAt: string;
  companyName: string;
  companyVat: string;
}

export const DEFAULT_PLANS: PlanItem[] = [
  {
    id: 'plan_starter_free',
    tier: 'free',
    name: 'Free Starter',
    description: 'Perfect for exploring Sophia Platform with foundational AI capabilities.',
    monthlyPrice: 0,
    yearlyPrice: 0,
    creditsIncluded: 1000,
    features: [
      '1,000 monthly credits included',
      '1 Workspace Member seat',
      'Standard AI Writer & 35+ prompt templates',
      'Chat Studio with GPT-4o Mini & Gemini 1.5 Flash',
      'CAT Translation Editor (up to 5,000 words)',
      'Community & Knowledge base support',
    ],
    isFeatured: false,
    seats: 1,
  },
  {
    id: 'plan_creator_pro',
    tier: 'pro',
    name: 'Professional Creator',
    description: 'Advanced multimedia AI, 4K image operations, and high-precision CAT translation.',
    monthlyPrice: 49,
    yearlyPrice: 470, // ~20% discount (saves $118/yr)
    creditsIncluded: 10000,
    features: [
      '10,000 monthly credits included',
      '5 Workspace Member seats with granular RBAC',
      'Flux 1.1 Pro & SD 3.5 Large Image Studio',
      'Interactive Canvas Inpainting & 4K AI Upscale',
      'Talking Avatar Studio with Lip-Sync & Neural Voices',
      'Full CAT Translation with Translation Memory & QA',
      'Developer API Access with 300 RPM rate limit',
      'Priority GPU execution queues',
    ],
    isFeatured: true,
    seats: 5,
  },
  {
    id: 'plan_scale_enterprise',
    tier: 'enterprise',
    name: 'Enterprise Scale',
    description: 'High-volume AI operations, custom voice cloning, and enterprise compliance SLA.',
    monthlyPrice: 199,
    yearlyPrice: 1900, // ~20% discount (saves $488/yr)
    creditsIncluded: 50000,
    features: [
      '50,000 monthly credits included',
      'Unlimited Workspace Member seats',
      'Custom Voice Cloning & Multi-Speaker DAW Studio',
      'Bring Your Own Key (BYOK) Zero-Markup execution',
      'High-throughput Developer API with 1,200 RPM limit',
      'Cloudflare D1 Dedicated Cryptographic Ledger',
      'Custom Corporate VAT invoices & Bank Transfer (VietQR)',
      '99.9% High Availability SLA & Dedicated Account Lead',
    ],
    isFeatured: false,
    seats: 999,
  },
];

export const DEFAULT_TOKEN_PACKS: TokenPackItem[] = [
  {
    id: 'pack_booster_2500',
    name: 'Quick Booster Pack',
    credits: 2500,
    price: 15,
  },
  {
    id: 'pack_booster_10000',
    name: 'Pro Creator Booster',
    credits: 10000,
    price: 50,
    badge: 'Popular',
  },
  {
    id: 'pack_booster_50000',
    name: 'Studio Scale Pack',
    credits: 50000,
    price: 200,
    badge: 'Best Value',
  },
];

export const SEED_ORDERS: Order[] = [
  {
    id: 'ord_2026_01HP9001',
    invoiceNumber: 'INV-2026-0042',
    workspaceId: 'ws_demo_primary',
    planId: 'plan_creator_pro',
    description: 'Professional Creator - Monthly Subscription',
    amountPaid: 49.0,
    taxAmount: 4.9,
    currency: 'USD',
    gatewayCode: 'stripe',
    gatewayTransactionId: 'ch_stripe_3Mv9x84Zp10Bqa7',
    status: 'paid',
    createdAt: new Date(Date.now() - 4 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9002',
    invoiceNumber: 'INV-2026-0038',
    workspaceId: 'ws_demo_primary',
    planId: 'pack_booster_10000',
    description: 'Pro Creator Booster (10,000 Credits)',
    amountPaid: 50.0,
    taxAmount: 5.0,
    currency: 'USD',
    gatewayCode: 'bank_transfer',
    gatewayTransactionId: 'VQR260819_110942',
    proofFileR2: 'receipts/vqr_proof_20260819.png',
    status: 'paid',
    createdAt: new Date(Date.now() - 14 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9003',
    invoiceNumber: 'INV-2026-0031',
    workspaceId: 'ws_demo_primary',
    planId: 'plan_creator_pro',
    description: 'Professional Creator - Monthly Subscription',
    amountPaid: 49.0,
    taxAmount: 4.9,
    currency: 'USD',
    gatewayCode: 'stripe',
    gatewayTransactionId: 'ch_stripe_3Mv9x83Zp09Aqa6',
    status: 'paid',
    createdAt: new Date(Date.now() - 34 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9004',
    invoiceNumber: 'INV-2026-0027',
    workspaceId: 'ws_demo_primary',
    planId: 'pack_booster_2500',
    description: 'Quick Booster Pack (2,500 Credits)',
    amountPaid: 15.0,
    taxAmount: 1.5,
    currency: 'USD',
    gatewayCode: 'stripe',
    gatewayTransactionId: 'ch_stripe_3Mv9x82Zp08Xqa5',
    status: 'paid',
    createdAt: new Date(Date.now() - 48 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9005',
    invoiceNumber: 'INV-2026-0021',
    workspaceId: 'ws_demo_primary',
    planId: 'pack_booster_50000',
    description: 'Studio Scale Pack (50,000 Credits)',
    amountPaid: 200.0,
    taxAmount: 20.0,
    currency: 'USD',
    gatewayCode: 'bank_transfer',
    gatewayTransactionId: 'VQR260710_091823',
    proofFileR2: 'receipts/vqr_proof_20260710.png',
    status: 'paid',
    createdAt: new Date(Date.now() - 65 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9006',
    invoiceNumber: 'INV-2026-0018',
    workspaceId: 'ws_demo_primary',
    planId: 'plan_creator_pro',
    description: 'Professional Creator - Monthly Subscription',
    amountPaid: 49.0,
    taxAmount: 4.9,
    currency: 'USD',
    gatewayCode: 'stripe',
    gatewayTransactionId: 'ch_stripe_3Mv9x81Zp07Wqa4',
    status: 'paid',
    createdAt: new Date(Date.now() - 95 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9007',
    invoiceNumber: 'INV-2026-0012',
    workspaceId: 'ws_demo_primary',
    planId: 'pack_booster_2500',
    description: 'Quick Booster Pack (2,500 Credits) - Bank Transfer',
    amountPaid: 15.0,
    taxAmount: 1.5,
    currency: 'USD',
    gatewayCode: 'bank_transfer',
    gatewayTransactionId: 'VQR260601_142301',
    status: 'pending',
    createdAt: new Date(Date.now() - 1 * 3600000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
  {
    id: 'ord_2026_01HP9008',
    invoiceNumber: 'INV-2026-0008',
    workspaceId: 'ws_demo_primary',
    planId: 'pack_booster_10000',
    description: 'Pro Creator Booster (10,000 Credits) - Refunded via Gateway',
    amountPaid: 50.0,
    taxAmount: 5.0,
    currency: 'USD',
    gatewayCode: 'stripe',
    gatewayTransactionId: 're_stripe_9X11z28P02Qq9',
    status: 'refunded',
    createdAt: new Date(Date.now() - 110 * 86400000).toISOString(),
    companyName: 'Four Elements Corp',
    companyVat: 'VN0317894210',
  },
];

export interface SubscriptionState {
  subscription: Subscription;
  plans: PlanItem[];
  tokenPacks: TokenPackItem[];
  orders: Order[];
  billingInterval: BillingInterval;
  orderStatusFilter: 'all' | 'paid' | 'pending' | 'refunded' | 'failed';
  searchQuery: string;
  selectedOrder: Order | null;
  isReceiptModalOpen: boolean;
  isChangePlanModalOpen: boolean;
  targetPlanForSwitch: PlanItem | null;
  isCancelModalOpen: boolean;
  isCustomerPortalLoading: boolean;

  // Actions
  setBillingInterval: (interval: BillingInterval) => void;
  setOrderStatusFilter: (status: 'all' | 'paid' | 'pending' | 'refunded' | 'failed') => void;
  setSearchQuery: (query: string) => void;
  setSelectedOrder: (order: Order | null) => void;
  setReceiptModalOpen: (isOpen: boolean) => void;
  openReceiptModal: (order: Order) => void;
  setChangePlanModalOpen: (isOpen: boolean, targetPlan?: PlanItem | null) => void;
  setCancelModalOpen: (isOpen: boolean) => void;
  changeSubscriptionPlan: (newPlanId: string) => void;
  cancelSubscription: () => void;
  resumeSubscription: () => void;
  openStripeCustomerPortal: () => Promise<void>;
  purchaseTokenPack: (packId: string) => void;
  getFilteredOrders: () => Order[];
  getSubscriptionUsagePercent: () => number;
  downloadInvoicePdf: (orderId: string) => string;
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: {
    id: 'sub_stripe_pro_019',
    workspaceId: 'ws_demo_primary',
    planId: 'plan_creator_pro',
    tier: 'pro',
    gatewayCode: 'stripe',
    gatewaySubId: 'sub_1Oq8ZvK2L9x0MnaQ',
    status: 'active',
    currentPeriodStart: new Date(Date.now() - 4 * 86400000).toISOString(),
    currentPeriodEnd: new Date(Date.now() + 26 * 86400000).toISOString(),
    cancelAtPeriodEnd: false,
    monthlyCredits: 10000,
    creditsUsedThisPeriod: 3420,
    paymentMethodPreview: {
      brand: 'Visa',
      last4: '4242',
      expMonth: 12,
      expYear: 2028,
    },
  },
  plans: DEFAULT_PLANS,
  tokenPacks: DEFAULT_TOKEN_PACKS,
  orders: SEED_ORDERS,
  billingInterval: 'monthly',
  orderStatusFilter: 'all',
  searchQuery: '',
  selectedOrder: null,
  isReceiptModalOpen: false,
  isChangePlanModalOpen: false,
  targetPlanForSwitch: null,
  isCancelModalOpen: false,
  isCustomerPortalLoading: false,

  setBillingInterval: (billingInterval) => set({ billingInterval }),
  setOrderStatusFilter: (orderStatusFilter) => set({ orderStatusFilter }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setSelectedOrder: (selectedOrder) => set({ selectedOrder }),
  setReceiptModalOpen: (isReceiptModalOpen) => set({ isReceiptModalOpen }),

  openReceiptModal: (order) => set({ selectedOrder: order, isReceiptModalOpen: true }),

  setChangePlanModalOpen: (isChangePlanModalOpen, targetPlanForSwitch = null) =>
    set({ isChangePlanModalOpen, targetPlanForSwitch }),

  setCancelModalOpen: (isCancelModalOpen) => set({ isCancelModalOpen }),

  changeSubscriptionPlan: (newPlanId) => {
    const target = get().plans.find((p) => p.id === newPlanId);
    if (!target) return;

    set((state) => ({
      subscription: {
        ...state.subscription,
        planId: target.id,
        tier: target.tier,
        monthlyCredits: target.creditsIncluded,
        cancelAtPeriodEnd: false,
      },
      isChangePlanModalOpen: false,
      targetPlanForSwitch: null,
    }));
  },

  cancelSubscription: () => {
    set((state) => ({
      subscription: {
        ...state.subscription,
        cancelAtPeriodEnd: true,
      },
      isCancelModalOpen: false,
    }));
  },

  resumeSubscription: () => {
    set((state) => ({
      subscription: {
        ...state.subscription,
        cancelAtPeriodEnd: false,
      },
    }));
  },

  openStripeCustomerPortal: async () => {
    set({ isCustomerPortalLoading: true });
    // Simulate Edge API call to create Stripe Customer Portal session
    await new Promise((resolve) => setTimeout(resolve, 800));
    set({ isCustomerPortalLoading: false });

    if (typeof window !== 'undefined') {
      window.open('https://billing.stripe.com/p/session/test_189230948123', '_blank');
    }
  },

  purchaseTokenPack: (packId) => {
    const pack = get().tokenPacks.find((p) => p.id === packId);
    if (!pack) return;

    const newOrder: Order = {
      id: `ord_2026_${crypto.randomUUID().slice(0, 8)}`,
      invoiceNumber: `INV-2026-${(get().orders.length + 1).toString().padStart(4, '0')}`,
      workspaceId: 'ws_demo_primary',
      planId: pack.id,
      description: `${pack.name} (${pack.credits.toLocaleString()} Credits)`,
      amountPaid: pack.price,
      taxAmount: Number((pack.price * 0.1).toFixed(2)),
      currency: 'USD',
      gatewayCode: 'stripe',
      gatewayTransactionId: `ch_stripe_${crypto.randomUUID().slice(0, 14)}`,
      status: 'paid',
      createdAt: new Date().toISOString(),
      companyName: 'Four Elements Corp',
      companyVat: 'VN0317894210',
    };

    set((state) => ({
      orders: [newOrder, ...state.orders],
    }));
  },

  getFilteredOrders: () => {
    const { orders, orderStatusFilter, searchQuery } = get();
    const query = searchQuery.trim().toLowerCase();

    return orders.filter((order) => {
      if (orderStatusFilter !== 'all' && order.status !== orderStatusFilter) {
        return false;
      }

      if (query) {
        const matchInvoice = order.invoiceNumber.toLowerCase().includes(query);
        const matchId = order.id.toLowerCase().includes(query);
        const matchDesc = order.description.toLowerCase().includes(query);
        const matchTx = order.gatewayTransactionId.toLowerCase().includes(query);
        if (!matchInvoice && !matchId && !matchDesc && !matchTx) {
          return false;
        }
      }

      return true;
    });
  },

  getSubscriptionUsagePercent: () => {
    const { subscription } = get();
    if (!subscription || subscription.monthlyCredits <= 0) return 0;
    const percent = (subscription.creditsUsedThisPeriod / subscription.monthlyCredits) * 100;
    return Math.min(100, Number(percent.toFixed(1)));
  },

  downloadInvoicePdf: (orderId) => {
    const order = get().orders.find((o) => o.id === orderId);
    if (!order) return '';

    // Generate compliant electronic invoice HTML document
    const invoiceHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${order.invoiceNumber} - Sophia Platform</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 40px; color: #1e293b; }
    .header { display: flex; justify-content: space-between; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; }
    .brand { font-size: 24px; font-weight: 700; color: #0f172a; }
    .invoice-title { font-size: 20px; font-weight: 600; text-align: right; }
    .meta { margin-top: 24px; display: flex; justify-content: space-between; }
    .table { width: 100%; border-collapse: collapse; margin-top: 32px; }
    .table th, .table td { border-bottom: 1px solid #e2e8f0; padding: 12px; text-align: left; }
    .table th { background: #f8fafc; font-size: 12px; text-transform: uppercase; color: #64748b; }
    .total-box { margin-top: 24px; text-align: right; }
    .badge { display: inline-block; padding: 4px 10px; border-radius: 9999px; font-size: 12px; font-weight: 600; background: #ecfdf5; color: #047857; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">Sophia Platform</div>
      <p style="font-size: 12px; color: #64748b; margin-top: 4px;">Four Elements Corp - Cloudflare Edge-Native SAAS</p>
    </div>
    <div>
      <div class="invoice-title">INVOICE</div>
      <div style="font-size: 13px; color: #64748b; margin-top: 4px;">${order.invoiceNumber}</div>
    </div>
  </div>
  <div class="meta">
    <div>
      <strong>Billed To:</strong><br>
      ${order.companyName}<br>
      VAT / Tax ID: ${order.companyVat}<br>
      Workspace: ws_demo_primary
    </div>
    <div style="text-align: right;">
      <strong>Payment Details:</strong><br>
      Date: ${new Date(order.createdAt).toLocaleDateString()}<br>
      Gateway: ${order.gatewayCode.toUpperCase()}<br>
      Transaction: ${order.gatewayTransactionId}<br>
      Status: <span class="badge">${order.status.toUpperCase()}</span>
    </div>
  </div>
  <table class="table">
    <thead>
      <tr>
        <th>Description</th>
        <th>Qty</th>
        <th>Unit Price</th>
        <th>Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${order.description}</td>
        <td>1</td>
        <td>$${order.amountPaid.toFixed(2)}</td>
        <td>$${order.amountPaid.toFixed(2)}</td>
      </tr>
    </tbody>
  </table>
  <div class="total-box">
    <p>Subtotal: $${(order.amountPaid - order.taxAmount).toFixed(2)}</p>
    <p>VAT (10%): $${order.taxAmount.toFixed(2)}</p>
    <h3 style="font-size: 18px; margin-top: 8px;">Total Paid: $${order.amountPaid.toFixed(2)} USD</h3>
  </div>
</body>
</html>`;

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const blob = new Blob([invoiceHtml], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${order.invoiceNumber}-receipt.html`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    return invoiceHtml;
  },
}));
